import { observer } from "mobx-react-lite";
import React, { useLayoutEffect } from "react";
import { Route, Routes } from "react-router";
import Organization from "../../../../models/Organization";
import { AuthStore } from "../../../../stores/AuthStore";
import { useStore } from "../../../../utils/useStore";
import OrganizationAgents from "./OrganizationAgents/OrganizationAgents";
import OrganizationAnalystGroups from "./OrganizationAnalystGroups/OrganizationAnalystGroups";
import OrganizationAnalysts from "./OrganizationAnalysts";
import OrganizationCustomerTypes from "./OrganizationCustomerTypes/OrganizationCustomerTypes";
import OrganizationDataSources from "./OrganizationDataSources/OrganizationDataSources";
import OrganizationDetail from "./OrganizationDetail";
import OrganizationKeyVaults from "./OrganizationKeyVaults";
import OrganizationLicense from "./OrganizationLicense";
import OrganizationModelControls from "./OrganizationModelControls/OrganizationModelControls";
import OrganizationModules from "./OrganizationModules/OrganizationModules";
import OrganizationMotfWrapper from "./OrganizationMotf/OrganizationMotfWrapper";
import OrganizationRoles from "./OrganizationRoles/OrganizationRoles";
import OrganizationServiceHierarchy from "./OrganizationServiceHierarchy/OrganizationServiceHierarchy";
import OrganizationStorage from "./OrganizationStorage";
import OrganizationUsers from "./OrganizationUsers/OrganizationUsers";
import OrganizationWorkflows from "./OrganizationWorkflows/OrganizationWorkflows";
import { OrganizationLLMPromptTemplates } from "./OrganizationLLMPromptTemplates/OrganizationLLMPromptTemplates";
import OrganizationChatReview from "./OrganizationChatReview/OrganizationChatReview";
import OrganizationMetadataManagement from "./OrganizationMetadataManagement/OrganizationMetadataManagement";
import { OrganizationKBoSettings } from "./OrganizationKBoSettings/OrganizationKBoSettings";
import OrganizationClientBrain from "./OrganizationClientBrain/OrganizationClientBrain";
import OrganizationResearchFindings from "./OrganizationResearchFindings/OrganizationResearchFindings";
import OrganizationModelFeedback from "./OrganizationModelFeedback/OrganizationModelFeedback";
import OrganizationAIModels from "./OrganizationAIModels/OrganizationAIModels";
import OrganizationUsageValue from "./OrganizationUsageValue/OrganizationUsageValue";
import OrganizationProactiveInsights from "./OrganizationProactiveInsights/OrganizationProactiveInsights";
import KboFlowDebug from "./KboFlowDebug/KboFlowDebug";
import FlowStepDebug from "./KboFlowStepDebug/FlowStepDebug";
import KboFlowRuns from "./KboFlowRuns/KboFlowRuns";
import KboFlowRunDetail from "./KboFlowRuns/KboFlowRunDetail";
import AgentSkillsPage from "components/AgentSkills/Views/AgentSkillsPage";
import AgentSkillEditorPage from "components/AgentSkills/Views/AgentSkillEditorPage";
import OrganizationSamlSettings from "./OrganizationSamlSettings/OrganizationSamlSettings";
import OrganizationKboFlowSubscriptions from "./OrganizationKboFlowSubscriptions/OrganizationKboFlowSubscriptions";
import { SendLogPage } from "components/KBo/Settings/SendLogPage";

type Props = {
    organizationId: string;
};

const OrganizationDetailRoutes: React.FC<Props> = observer(
    ({ organizationId }) => {
        const authStore = useStore(AuthStore);
        const orgStore = authStore.orgStore;

        useLayoutEffect(() => {
            orgStore?.onActiveOrgIdChange(organizationId);
            orgStore?.setGlobalOrganization(
                orgStore.organizations?.find(
                    (org) => org.id === organizationId,
                )! as Organization,
            );
        }, [orgStore, organizationId]);

        return (
            <Routes>
                <Route path="license" element={<OrganizationLicense />} />
                <Route
                    path="modules/*"
                    element={<OrganizationModules id={organizationId} />}
                />
                <Route
                    path="service-hierarchy"
                    element={
                        <OrganizationServiceHierarchy id={organizationId} />
                    }
                />
                <Route
                    path="users/*"
                    element={<OrganizationUsers id={organizationId} />}
                />
                <Route path="analysts" element={<OrganizationAnalysts />} />
                <Route
                    path="analyst-groups"
                    element={<OrganizationAnalystGroups id={organizationId} />}
                />
                <Route
                    path="roles"
                    element={<OrganizationRoles id={organizationId} />}
                />
                {authStore.canUserView("Usage & Value") && (
                    <Route
                        path="usage-value"
                        element={<OrganizationUsageValue id={organizationId} />}
                    />
                )}
                <Route path="storage" element={<OrganizationStorage />} />
                <Route path="key-vaults" element={<OrganizationKeyVaults />} />
                <Route
                    path="contact-types"
                    element={<OrganizationCustomerTypes id={organizationId} />}
                />
                <Route
                    path="model-controls"
                    element={<OrganizationModelControls id={organizationId} />}
                />
                <Route
                    path="datasources"
                    element={<OrganizationDataSources />}
                />
                <Route
                    path="metadata-management"
                    element={<OrganizationMetadataManagement />}
                />
                {(authStore.isUserUltra() ||
                    authStore.canUserView("Client Brain")) && (
                    <Route
                        path="kbo-flow-subscriptions"
                        element={
                            <OrganizationKboFlowSubscriptions
                                id={organizationId}
                            />
                        }
                    />
                )}
                {authStore.isUserUltra() && (
                    <Route
                        path="proactive-insights"
                        element={
                            <OrganizationProactiveInsights
                                id={organizationId}
                            />
                        }
                    />
                )}
                <Route
                    path="agents"
                    element={<OrganizationAgents id={organizationId} />}
                />
                <Route
                    path="workflows"
                    element={<OrganizationWorkflows id={organizationId} />}
                />
                <Route
                    path="motf/*"
                    element={<OrganizationMotfWrapper id={organizationId} />}
                />
                <Route
                    path="llm-prompt-templates"
                    element={<OrganizationLLMPromptTemplates />}
                />
                {authStore.isUserUltra() && (
                    <Route
                        path="kbo-settings"
                        element={<OrganizationKBoSettings />}
                    />
                )}
                {authStore.isUserUltra() && (
                    <Route
                        path="client-brain"
                        element={<OrganizationClientBrain canEdit />}
                    />
                )}
                {authStore.isUserUltra() && (
                    <Route
                        path="research-findings"
                        element={<OrganizationResearchFindings />}
                    />
                )}
                {authStore.canUserEdit("Organization Details") && (
                    <Route
                        path="saml-sso"
                        element={
                            <OrganizationSamlSettings id={organizationId} />
                        }
                    />
                )}
                <Route path="agent-skills" element={<AgentSkillsPage />} />
                <Route
                    path="agent-skills/new"
                    element={<AgentSkillEditorPage />}
                />
                <Route
                    path="agent-skills/:skillId"
                    element={<AgentSkillEditorPage />}
                />
                <Route
                    path="chat-review"
                    element={<OrganizationChatReview id={organizationId} />}
                />
                <Route
                    path="chat-review/:chatId"
                    element={<OrganizationChatReview id={organizationId} />}
                />
                {authStore.isUserUltra() && (
                    <Route path="model-feedback" element={<OrganizationModelFeedback key={organizationId} id={organizationId} />} />
                )}
                {authStore.isUserUltra() && (
                    <Route path="ai-models" element={<OrganizationAIModels />} />
                )}
                {authStore.isUserUltra() && (
                    <Route
                        path="kbo-flow-runs"
                        element={<KboFlowRuns orgId={organizationId} />}
                    />
                )}
                {authStore.isUserUltra() && (
                    <Route
                        path="send-log"
                        element={
                            <SendLogPage adminOrganizationId={organizationId} />
                        }
                    />
                )}
                {authStore.isUserUltra() && (
                    <Route
                        path="kbo-flow-runs/:runId"
                        element={<KboFlowRunDetail orgId={organizationId} />}
                    />
                )}
                {authStore.isUserUltra() && (
                    <Route
                        path="kbo-flow-debug"
                        element={<KboFlowDebug orgId={organizationId} />}
                    />
                )}
                {authStore.isUserUltra() && (
                    <Route
                        path="flow-step-debug"
                        element={<FlowStepDebug orgId={organizationId} />}
                    />
                )}
                <Route
                    path="*"
                    element={<OrganizationDetail id={organizationId} />}
                />
            </Routes>
        );
    },
);

export default OrganizationDetailRoutes;
